import React, { useEffect, useState } from "react";
import { Store, CheckCircle2, Banknote, TrendingUp, Eye, CalendarClock, UserPlus, Globe, Percent } from "lucide-react";
import { Link } from "react-router-dom";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";
import { API, money, fmtDate } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { StatusBadge } from "../../components/StatusBadge";
import { WorldMap } from "../../components/WorldMap";

function Stat({ icon: Icon, label, value, tone = "text-slate-900", testid }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-mono uppercase tracking-wider text-slate-500">{label}</span>
        <Icon className="h-5 w-5 text-amber-600" />
      </div>
      <div className={`mt-3 font-heading text-2xl font-bold ${tone}`}>{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [an, setAn] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    API.get("/admin/overview").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setAn(null);
    API.get(`/admin/analytics?days=${days}`).then((r) => setAn(r.data)).catch(() => {});
  }, [days]);

  if (!data) return <p className="text-slate-500">{t("common.loading")}</p>;

  const series = (an?.series || []).map((d) => ({ ...d, label: d.day.slice(5) }));
  const rangeLabel = `${days}${t("admin.days_short")}`;
  const conv = an && an.visits_period
    ? `${Math.min(100, (an.signups_period / an.visits_period) * 100).toFixed(1)}%`
    : "—";

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Store} label={t("admin.total_shops")} value={data.total_shops} testid="admin-stat-shops" />
        <Stat icon={CheckCircle2} label={t("admin.active_subs")} value={data.active_shops} tone="text-emerald-700" testid="admin-stat-active" />
        <Stat icon={Banknote} label={t("admin.pending_transfers")} value={`${data.pending_payments} · ${money(data.pending_amount)}`} tone="text-amber-600" testid="admin-stat-pending" />
        <Stat icon={TrendingUp} label={t("admin.mrr")} value={money(data.mrr)} tone="text-emerald-700" testid="admin-stat-mrr" />
      </div>

      <div data-testid="admin-analytics">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h3 className="font-heading text-lg font-medium text-slate-900 flex items-center gap-2">
            <Eye className="h-5 w-5 text-red-600" />{t("admin.analytics")}
          </h3>
          <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1" data-testid="analytics-range-toggle">
            {[7, 30, 90].map((d) => (
              <button key={d} onClick={() => setDays(d)} data-testid={`range-${d}`}
                className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${days === d ? "bg-[#111111] text-white" : "text-slate-600 hover:text-slate-900"}`}>
                {d}{t("admin.days_short")}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={Eye} label={t("admin.total_visits")} value={an?.total_visits ?? "—"} tone="text-red-700" testid="admin-stat-visits" />
          <Stat icon={TrendingUp} label={`${t("admin.visits_range")} (${rangeLabel})`} value={an?.visits_period ?? "—"} tone="text-red-700" testid="admin-stat-visits-period" />
          <Stat icon={CalendarClock} label={t("admin.visits_today")} value={an?.visits_today ?? "—"} testid="admin-stat-visits-today" />
          <Stat icon={UserPlus} label={t("admin.signups_today")} value={an?.signups_today ?? "—"} tone="text-amber-600" testid="admin-stat-signups-today" />
        </div>

        <div className="mt-4 grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5" data-testid="admin-visits-chart">
            <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-4">{t("admin.visits_signups")}</div>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={series} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#dc2626" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#dc2626" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gs" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#d97706" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#d97706" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} minTickGap={16} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} width={32} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
                <Area type="monotone" dataKey="visits" name={t("admin.total_visits")} stroke="#dc2626" strokeWidth={2} fill="url(#gv)" />
                <Area type="monotone" dataKey="signups" name={t("admin.signups")} stroke="#d97706" strokeWidth={2} fill="url(#gs)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="admin-sources-chart">
            <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-4">{t("admin.top_sources")}</div>
            {(an?.sources || []).length === 0 ? (
              <p className="text-sm text-slate-500">{t("admin.no_traffic")}</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={an.sources} layout="vertical" margin={{ top: 0, right: 12, left: 8, bottom: 0 }}>
                  <XAxis type="number" hide allowDecimals={false} />
                  <YAxis type="category" dataKey="source" width={92} tick={{ fontSize: 11, fill: "#334155" }} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} cursor={{ fill: "#f1f5f9" }} />
                  <Bar dataKey="count" name={t("admin.total_visits")} fill="#dc2626" radius={[0, 4, 4, 0]} barSize={16} />
                </BarChart>
              </ResponsiveContainer>
            )}
            {(an?.campaigns || []).length > 0 && (
              <div className="mt-4 border-t border-slate-100 pt-3" data-testid="admin-campaigns">
                <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">{t("admin.top_campaigns")}</div>
                <div className="flex flex-wrap gap-2">
                  {an.campaigns.map((c) => (
                    <span key={c.campaign} className="text-xs bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 text-amber-800 font-medium" data-testid={`admin-campaign-${c.campaign}`}>
                      {c.campaign} · {c.count}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-br from-red-600 to-red-700 text-white rounded-xl p-5 flex items-center justify-between shadow-sm" data-testid="admin-conversion-tile">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-red-100">{t("admin.conversion")} ({rangeLabel})</div>
          <div className="mt-2 font-heading text-3xl font-bold" data-testid="admin-conversion-value">{conv}</div>
          <div className="text-xs text-red-100/90 mt-1">
            {t("admin.conversion_hint")}{an && an.visits_period ? ` · ${an.signups_period}/${an.visits_period}` : ""}
          </div>
        </div>
        <Percent className="h-10 w-10 text-red-200/60" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="admin-map-card">
        <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-4 flex items-center gap-1.5">
          <Globe className="h-3.5 w-3.5" />{t("admin.visitor_map")}
        </div>
        {(an?.countries || []).length === 0 ? (
          <p className="text-sm text-slate-500">{t("admin.no_traffic")}</p>
        ) : (
          <div className="flex flex-col lg:flex-row items-center gap-5">
            <div className="flex-1 w-full min-w-0"><WorldMap data={an.countries} height={340} /></div>
            <div className="flex flex-row lg:flex-col flex-wrap gap-2 shrink-0">
              {an.countries.map((c) => (
                <span key={c.country} className="text-xs bg-slate-100 rounded-full px-2.5 py-1 text-slate-700 font-medium" data-testid={`admin-map-legend-${c.country}`}>
                  {c.country} · {c.count}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-heading text-lg font-medium text-slate-900 mb-4">{t("admin.recent")}</h3>
        {data.recent_payments.length === 0 && <p className="text-sm text-slate-500">{t("common.none")}</p>}
        <div className="space-y-2">
          {data.recent_payments.map((p) => (
            <Link to="/admin/payments" key={p.id} data-testid={`recent-payment-${p.reference}`}
              className="flex items-center justify-between border border-slate-200 rounded-lg px-4 py-3 hover:border-amber-400 transition-colors">
              <div>
                <span className="font-medium text-slate-900">{p.shop_name}</span>
                <span className="text-slate-400 text-sm ml-2">{p.plan_name} · {p.billing_cycle}</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="font-mono text-sm">{money(p.amount)}</span>
                <span className="text-xs text-slate-500">{fmtDate(p.created_at)}</span>
                <StatusBadge status={p.status} />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
